/**
 * KAN-766: режим "на весь экран" для блока страницы (таблица CRM).
 *
 * Хранит флаг режима; в развёрнутом режиме:
 * - Esc сворачивает, если событие ещё не обработано (defaultPrevented) и в DOM
 *   нет открытой модалки ([role=dialog] / [role=alertdialog]). Radix Dialog
 *   ловит Esc сам и закрывает только себя: модалка ещё в DOM, пока идёт
 *   то же событие, поэтому блок не сворачивается вместе с ней;
 * - прокрутка body заблокирована (overflow: hidden), прежнее значение
 *   возвращается при выходе и размонтировании.
 * KAN-910: controlled - флаг хранится снаружи (адрес CRM ?full=1, useCrmFullscreen).
 */
import { useCallback, useEffect, useState } from 'react';

export interface FullscreenControl {
  value: boolean;
  set: (value: boolean) => void;
}

const OPEN_MODAL_SELECTOR = '[role="dialog"], [role="alertdialog"]';

export function useFullscreenToggle(initial = false, controlled?: FullscreenControl) {
  const [own, setOwn] = useState(initial);
  const isFullscreen = controlled ? controlled.value : own;
  const setControlled = controlled?.set;
  const setFullscreen = useCallback(
    (value: boolean) => (setControlled ? setControlled(value) : setOwn(value)),
    [setControlled]
  );

  const toggle = useCallback(() => setFullscreen(!isFullscreen), [isFullscreen, setFullscreen]);
  const exit = useCallback(() => setFullscreen(false), [setFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector(OPEN_MODAL_SELECTOR)) return;
      setFullscreen(false);
    };
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      body.style.overflow = previousOverflow;
    };
  }, [isFullscreen, setFullscreen]);

  return { isFullscreen, toggle, exit };
}
